import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const BACKOFFICE = "https://agente.pulso33.com";
const HOST = "rrlexjuristas.com";
const TTL_MS = 5 * 60_000;

export interface PageSeo {
  title?: string;
  description?: string;
  canonical?: string;
  robots?: string;
  og?: Partial<Record<"type" | "title" | "description" | "image" | "url" | "siteName" | "locale", string>>;
  twitter?: Partial<Record<"card" | "title" | "description" | "image", string>>;
  alternates?: Array<{ hreflang: string; href: string }>;
  /** Pre-serialized JSON-LD blocks (one per <script>). */
  jsonLd: string[];
}

const cache = new Map<string, { at: number; value: PageSeo | null }>();

export const getPageSeo = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z.object({ path: z.string().max(500), locale: z.enum(["es", "en", "pt"]) }).parse(d),
  )
  .handler(async ({ data }): Promise<{ seo: PageSeo | null }> => {
    const path = data.path.split("?")[0] || "/";
    const key = `${path}|${data.locale}`;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) return { seo: hit.value };
    try {
      const url = `${BACKOFFICE}/api/public/v1/seo?host=${HOST}&path=${encodeURIComponent(path)}&locale=${data.locale}`;
      const res = await fetch(url, { signal: AbortSignal.timeout(4000) });
      if (!res.ok) throw new Error(`seo ${res.status}`);
      const json = (await res.json()) as { meta?: Omit<PageSeo, "jsonLd">; jsonLd?: unknown };
      if (!json.meta?.title) throw new Error("seo: empty meta");
      const value: PageSeo = {
        ...json.meta,
        jsonLd: Array.isArray(json.jsonLd)
          ? json.jsonLd.filter((b) => b && typeof b === "object").map((b) => JSON.stringify(b))
          : [],
      };
      cache.set(key, { at: Date.now(), value });
      return { seo: value };
    } catch (e) {
      console.warn("getPageSeo failed, using fallback", key, e);
      // Short negative cache so a slow backoffice doesn't add 4 s to every request.
      cache.set(key, { at: Date.now() - TTL_MS + 30_000, value: null });
      return { seo: null };
    }
  });
